import { forwardRef, memo, useCallback, useContext } from 'react';
import type { RefAttributes } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import clsx from 'clsx';

import {
  groupRowSelectedClassname,
  rowClassname,
  rowSelectedClassname,
  dropOverRowClassName,
  dragingRowClassName,
  rdgDragPreviewClassname,
  rowCellShowMenuClassname,
  rowIsAddClassname,
  rowIsDeleteClassname,
  rowMainSelectClassname,
} from './style';
import Cell from './Cell';
import EditCell from './EditCell';
import type { RowRendererProps, SelectedCellProps, getContextMenuConfig, ContextMenuRender } from './types';
import { isEqual } from 'lodash';
import { RowStore } from './store/rowStore';
import { ContextMenuStore } from './store/contextMenu';

export interface CellContextProps {
  setShowMenuRowIdx: ((rowIdx: number, isShow: boolean) => void) | undefined;
  getContextMenuConfig?: getContextMenuConfig<any>;
  ContextMenuRender?: ContextMenuRender<any>;
}



const DRAG_KEY = 'DndRowIndexDnd';

function Row<R, SR = unknown>({
  cellRenderer: CellRenderer = Cell,
  className,
  rowIdx,
  isRowSelected,
  isMainSelectedRow,
  isLastRow,
  columnMetrics,
  selectedColumns,
  draggedOverCellIdx,
  lastFrozenColumnIndex,
  row,
  viewportColumns,
  selectedCellProps,
  selectedRange,
  getModifiedColumns,
  isAdd,
  isDeleted,
  onRowClick,
  rowClass,
  setDraggedOverRowIdx,
  setShowMenuRowIdx,
  getContextMenuConfig,
  ContextMenuRender,
  lazyStore,
  setMouseDown,
  onMouseEnter,
  top,
  onRowChange,
  onRowReorder,
  onGridSelect,
  enableRowRecord,
  totalColumnWidth,
  isShowRowMenu,
  'aria-rowindex': ariaRowIndex,
  'aria-selected': ariaSelected,
  ...props
}: RowRendererProps<R, SR> & CellContextProps, ref: React.Ref<HTMLDivElement>) {
  function handleDragEnter(event: React.MouseEvent<HTMLDivElement>) {
    setDraggedOverRowIdx?.(rowIdx);
    onMouseEnter?.(event);
  }
  const dragCollect = useCallback((monitor) => {
    return {
      isDragging: monitor.isDragging()
    }
  }, [])

  const dropCollect = useCallback((monitor) => {
    return {
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    }
  }, [])

  const dropFunc = useCallback(({ index, type, row: dragRow }: { index: number, type: string, row: R }) => {
    if (type === DRAG_KEY) {
      onRowReorder?.(index, rowIdx);
    }
  }, [onRowReorder])

  const drag = useDrag({
    type: DRAG_KEY,
    item: { index: rowIdx, type: DRAG_KEY, row },
    collect: dragCollect
  });
  const drop = useDrop({
    accept: DRAG_KEY,
    drop: dropFunc,
    collect: dropCollect
  });

  className = clsx(
    rowClassname,
    `rdg-row-${rowIdx % 2 === 0 ? 'even' : 'odd'}`,
    {
      [rowSelectedClassname]: isRowSelected,
      [groupRowSelectedClassname]: selectedCellProps?.idx === -1,
      [rowCellShowMenuClassname]: isShowRowMenu,
      [rowIsAddClassname]: isAdd,
      [rowIsDeleteClassname]: isDeleted,
      [rowMainSelectClassname]: isMainSelectedRow,
    },
    drop[0].isOver ? dropOverRowClassName : null,
    drag[0].isDragging ? dragingRowClassName : null,
    rowClass?.(row),
    className
  );

  const cells = [];
  for (let index = 0; index < viewportColumns.length; index++) {
    const column = viewportColumns[index];
    const isCellFirstSelected = selectedCellProps?.idx === column.idx;

    if (selectedCellProps?.mode === 'EDIT' && isCellFirstSelected) {
      cells.push(
        <EditCell<R, SR>
          key={column.key}
          rowIdx={rowIdx}
          column={column}
          width={columnMetrics.get(column)?.width}
          row={row}
          onKeyDown={selectedCellProps.onKeyDown}
          editorProps={selectedCellProps.editorProps}
        />
      );
      continue;
    }
    const [minIdx, maxIdx] = [selectedRange.columnIdx, selectedRange.endColumnIdx].sort((a, b) => a - b)
    const [minRowIdx, maxRowIdx] = [selectedRange.rowIdx, selectedRange.endRowIdx].sort((a, b) => a - b)
    const isCellRangeSelected = column.idx >= minIdx && column.idx <= maxIdx && rowIdx >= minRowIdx && rowIdx <= maxRowIdx;
    cells.push(
      <CellRenderer
        key={column.key + '-' + rowIdx}
        width={columnMetrics.get(column)?.width}
        rowIdx={rowIdx}
        column={column}
        columnIdx={column.idx}
        row={row}
        enableRowRecord={enableRowRecord}
        isColumnSelected={selectedColumns.has(column.key)}
        drop={drop}
        drag={drag}
        isModified={getModifiedColumns(row)?.includes(column.key)}
        lazyStore={lazyStore}
        setMouseDown={setMouseDown}
        isRowCellSelected={rowIdx >= minRowIdx && rowIdx <= maxRowIdx}
        isDraggedOver={draggedOverCellIdx === column.idx}
        isCellSelected={isCellRangeSelected}
        isCellFirstSelected={isCellFirstSelected}
        isRowSelected={isRowSelected}
        dragHandleProps={isCellFirstSelected ? (selectedCellProps as SelectedCellProps).dragHandleProps : undefined}
        onFocus={isCellFirstSelected ? (selectedCellProps as SelectedCellProps).onFocus : undefined}
        onKeyDown={isCellFirstSelected ? selectedCellProps!.onKeyDown : undefined}
        onGridSelect={onGridSelect}
        onRowClick={onRowClick}
        onRowChange={onRowChange}
        onRowReorder={onRowReorder}
        selectedRange={selectedRange}
      />
    );
  }



  return (
    <div
      role="row"
      aria-rowindex={ariaRowIndex}
      aria-selected={ariaSelected}
      ref={ref}
      className={className}
      onMouseEnter={handleDragEnter}
      style={{ top }}
      {...props}
    >
      <div ref={drag[2]} className={clsx('rdg-drag-preview', rdgDragPreviewClassname)}></div>
      {cells}
    </div>
  );
}

const MemoRow = memo(forwardRef(Row), (prevProps, nextProps) => {
  const [oldMinRowIdx, oldMaxRowIdx] = [prevProps.selectedRange.rowIdx, prevProps.selectedRange.endRowIdx].sort((a, b) => a - b);
  const [newMinRowIdx, newMaxRowIdx] = [nextProps.selectedRange.rowIdx, nextProps.selectedRange.endRowIdx].sort((a, b) => a - b);
  const isOldInRange = oldMinRowIdx <= prevProps.rowIdx && oldMaxRowIdx >= prevProps.rowIdx;
  const isNewInRange = newMinRowIdx <= prevProps.rowIdx && newMaxRowIdx >= prevProps.rowIdx;
  if (
    (isOldInRange || isNewInRange) && prevProps.selectedRange != nextProps.selectedRange
  ) {
    /**
     * 判断是否在select row的影响里，不在的话再更新，提升性能
     */
    return false;
  }

  return isEqual({ ...prevProps, selectedRange: null }, { ...nextProps, selectedRange: null });
}) as <R, SR = unknown>(props: RowRendererProps<R, SR> & RefAttributes<HTMLDivElement> & CellContextProps) => JSX.Element;

export default function <R, SR>(props: RowRendererProps<R, SR> & RefAttributes<HTMLDivElement>) {
  const { showMenuRowIdx, setShowMenuRowIdx } = useContext(RowStore);
  const contextMenuStore = useContext(ContextMenuStore);
  const { getContextMenuConfig, ContextMenuRender } = contextMenuStore;
  return <MemoRow getContextMenuConfig={getContextMenuConfig} ContextMenuRender={ContextMenuRender} setShowMenuRowIdx={setShowMenuRowIdx} isShowRowMenu={showMenuRowIdx === props.rowIdx}  {...props} />
}

